import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Box,
  Stack,
  Fade,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BoltIcon from '@mui/icons-material/Bolt';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { requestFcmPermissionAndRegister } from '../../services/fcmNotificationService';

export const NOTIFICATION_POPUP_DISMISSED_KEY = 'oni_notification_popup_dismissed';

const NotificationPermissionModal = ({ forceOpen = false, onClose }) => {
  const [open, setOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [resultState, setResultState] = useState(null);

  useEffect(() => {
    // Notifications only supported in window with Notification API
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }

    // If forced to open (e.g. from navbar click)
    if (forceOpen) {
      setOpen(true);
      return;
    }

    // Only prompt when permission is in 'default' state
    const currentPermission = Notification.permission;
    if (currentPermission !== 'default') {
      return;
    }

    // Check if user dismissed it during this session
    try {
      const isDismissed = sessionStorage.getItem(NOTIFICATION_POPUP_DISMISSED_KEY) === 'true';
      if (isDismissed) {
        return;
      }
    } catch {
      // ignore storage access issues
    }

    // Slight delay so the doctor sees their dashboard first before the popup appears
    const timer = setTimeout(() => {
      setOpen(true);
    }, 1200);

    return () => clearTimeout(timer);
  }, [forceOpen]);

  const handleClose = () => {
    try {
      sessionStorage.setItem(NOTIFICATION_POPUP_DISMISSED_KEY, 'true');
    } catch {
      // ignore
    }
    setOpen(false);
    if (onClose) onClose();
  };

  const handleEnableNotifications = async () => {
    setRequesting(true);
    setResultState(null);

    try {
      const res = await requestFcmPermissionAndRegister();

      if (res?.success) {
        setResultState('success');
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else if (Notification.permission === 'denied' || res?.reason === 'PERMISSION_DENIED') {
        setResultState('denied');
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        // user may have dismissed native dialog or closed it
        handleClose();
      }
    } catch (err) {
      console.error('Error enabling notifications from popup:', err);
      handleClose();
    } finally {
      setRequesting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            backdropFilter: 'blur(4px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          background: '#FFF',
          color: '#FFFFFF',
          p: { xs: 2.5, sm: 3 },
          position: 'relative',
          overflow: 'hidden',
        },
      }}
    >
      {/* Decorative top-right gradient glow */}
      <Box
        sx={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 140,
          height: 140,
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />

      {/* Close button */}
      <IconButton
        onClick={handleClose}
        size="small"
        aria-label="Close notification popup"
        sx={{
          position: 'absolute',
          top: 14,
          right: 14,
          color: 'black',
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ p: 0, pt: 1 }}>
        <Stack spacing={2.5} alignItems="center" textAlign="center">
          {/* Header glowing icon */}
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8A4BFF 0%, #3B82F6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 20px rgba(138, 75, 255, 0.55)',
            }}
          >
            <NotificationsActiveIcon sx={{ color: '#FFFFFF', fontSize: 30 }} />
          </Box>

          {/* Title & subtitle */}
          <Box>
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" mb={0.75}>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 20 }, color: '#808080' }}>
                Enable Consultation Alerts
              </Typography>
              <AutoAwesomeIcon sx={{ color: '#FCD34D', fontSize: 18 }} />
            </Stack>
            <Typography variant="body2" sx={{ color: '#808080', fontSize: 13.5, lineHeight: 1.5 }}>
              Stay updated the moment AI finishes extracting clinical notes, prescriptions, and diagnoses from your consultations.
            </Typography>
          </Box>

          {/* Benefits list */}
          <Stack spacing={1.25} sx={{ width: '100%', textAlign: 'left' }}>
            <Box
              sx={{
                p: 1.25,
                px: 1.75,
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <BoltIcon sx={{ color: '#F59E0B', fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontSize: 12.5, color: '#808080', fontWeight: 500 }}>
                Instant notifications when AI consultation notes are ready
              </Typography>
            </Box>
            
          </Stack>

          {/* Result state feedback */}
          {resultState === 'success' && (
            <Fade in={true}>
              <Alert
                icon={<CheckCircleOutlineIcon fontSize="inherit" />}
                severity="success"
                sx={{
                  width: '100%',
                  bgcolor: 'rgba(16, 185, 129, 0.15)',
                  color: '#34D399',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  fontSize: 13,
                  py: 0.5,
                }}
              >
                Notifications enabled! You'll receive AI review alerts.
              </Alert>
            </Fade>
          )}

          {resultState === 'denied' && (
            <Fade in={true}>
              <Alert
                severity="warning"
                sx={{
                  width: '100%',
                  bgcolor: 'rgba(245, 158, 11, 0.15)',
                  color: '#FBBF24',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  fontSize: 12.5,
                  py: 0.5,
                }}
              >
                Notifications are blocked. You can allow them later in browser site settings (lock icon in address bar).
              </Alert>
            </Fade>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 0, pt: 3, justifyContent: 'space-between', gap: 1 }}>
        <Button
          variant="text"
          onClick={handleClose}
          disabled={requesting}
          data-testid="notification-modal-later-btn"
          sx={{
            color: '#808080',
            textTransform: 'none',
            fontSize: 13.5,
            fontWeight: 500,
            px: 2,
            border:'1px solid black'
          }}
        >
          Maybe Later
        </Button>

        <Button
          variant="contained"
          onClick={handleEnableNotifications}
          disabled={requesting || resultState === 'success'}
          data-testid="notification-modal-enable-btn"
          startIcon={
            requesting ? (
              <CircularProgress size={16} sx={{ color: '#FFFFFF' }} />
            ) : (
              <NotificationsActiveIcon sx={{ fontSize: 18 }} />
            )
          }
          sx={{
            background: '#F56423',
            color: '#FFFFFF',
            fontWeight: 600,
            fontSize: 13.5,
            textTransform: 'none',
            px: 2.5,
            py: 1,
            borderRadius: 2,
            '&.Mui-disabled': {
              background: 'rgba(255, 255, 255, 0.12)',
              color: 'rgba(255, 255, 255, 0.4)',
            },
          }}
        >
          {requesting ? 'Enabling...' : 'Enable Notifications'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotificationPermissionModal;
